import { z } from "zod";
import { metaSchema } from "./commun.zod";
import { EspeceSchema } from "./espece.zod";
import { InventaireSchema } from "./inventaire.zod";
import { UserSchema } from "./user.zod";

export const PlantationBaseSchema = z.object({
  hauteur: z.number().int().positive("La hauteur doit être un nombre positif").nullable().optional(),
  diametre: z.number().int().positive("Le diamètre doit être un nombre positif").nullable().optional(),
  count_subject: z.number().int().positive("Le nombre de sujets doit être positif").nullable().optional(),
  ville: z.string().max(100, "La ville ne peut pas dépasser 100 caractères").nullable().optional(),
  pays: z.string().max(255, "Le pays ne peut pas dépasser 255 caractères").nullable().optional(),
  address: z.string().max(100, "L'adresse ne peut pas dépasser 100 caractères").nullable().optional(),
  date_echeance: z.string().nullable().optional(),
  date_report: z.string().nullable().optional(),
  espece_id: z.number().int().positive("L'ID de l'espèce doit être un nombre positif"),
  user_validate_id: z.number().int().positive("L'ID de l'utilisateur validateur doit être un nombre positif").nullable().optional(),
  user_added_id: z.number().int().positive("L'ID de l'utilisateur qui a ajouté doit être un nombre positif"),
  inventory_id: z.number().int().positive("L'ID de l'inventaire doit être un nombre positif").nullable().optional(),
  espece: EspeceSchema,
  inventaire: InventaireSchema,
  user_validate: UserSchema,
  user_added: UserSchema,
});

export const PlantationSchema = PlantationBaseSchema.extend({
  id: z.number().int().positive(),
  created_at: z.date().nullable().optional(),
  updated_at: z.date().nullable().optional(),
});

export const PlantationCreateSchema = PlantationBaseSchema.pick({
  hauteur: true,
  diametre: true,
  count_subject: true,
  ville: true,
  pays: true,
  address: true,
  date_echeance: true,
  date_report: true,
  espece_id: true,
  inventory_id: true,
});

export const PlantationUpdateSchema = z.object({
  id: z.number().int().positive(),
  hauteur: z.number().int().positive("La hauteur doit être un nombre positif").nullable().optional(),
  diametre: z.number().int().positive("Le diamètre doit être un nombre positif").nullable().optional(),
  count_subject: z.number().int().positive("Le nombre de sujets doit être positif").nullable().optional(),
  ville: z.string().max(100, "La ville ne peut pas dépasser 100 caractères").nullable().optional(),
  pays: z.string().max(255, "Le pays ne peut pas dépasser 255 caractères").nullable().optional(),
  address: z.string().max(100, "L'adresse ne peut pas dépasser 100 caractères").nullable().optional(),
  date_echeance: z.date().nullable().optional(),
  date_report: z.date().nullable().optional(),
  espece_id: z.number().int().positive("L'ID de l'espèce doit être un nombre positif").optional(),
  user_validate_id: z.number().int().positive("L'ID de l'utilisateur validateur doit être un nombre positif").nullable().optional(),
  user_added_id: z.number().int().positive("L'ID de l'utilisateur qui a ajouté doit être un nombre positif").optional(),
  inventory_id: z.number().int().positive("L'ID de l'inventaire doit être un nombre positif").nullable().optional(),
});

export const PlantationListResponseSchema = z.object({
  data: z.array(PlantationSchema),
  meta: metaSchema,

});

export const PlantationResponseSchema = z.object({
  data: PlantationSchema,
  message: z.string().optional(),
});

export const PlantationSelectSchema = z.object({
  id: z.number().int().positive().optional(),
  espece_id: z.number().int().positive().optional(),
  user_added_id: z.number().int().positive().optional(),
  user_validate_id: z.number().int().positive().optional(),
  inventory_id: z.number().int().positive().optional(),
  ville: z.string().optional(),
  pays: z.string().optional(),
  address: z.string().optional(),
  date_echeance_from: z.date().optional(),
  date_echeance_to: z.date().optional(),
  limit: z.number().default(10),
  page: z.number().default(1),
  search: z.string().optional(),
});

export type Plantation = z.infer<typeof PlantationSchema>;
export type PlantationCreateInput = z.infer<typeof PlantationCreateSchema>;
export type PlantationUpdateInput = z.infer<typeof PlantationUpdateSchema>;
export type PlantationSelectInput = z.infer<typeof PlantationSelectSchema>;

// Form-specific types for frontend
export type PlantationFormData = Omit<PlantationCreateInput, 'user_validate_id' | 'user_added_id'> & {
  user_validate_id?: number;
  user_added_id?: number;
};

export type PlantationEditFormData = Omit<PlantationUpdateInput, 'id'>;

// Utility types for different UI states
export type PlantationDisplayItem = Pick<Plantation, 'id' | 'address' | 'ville' | 'count_subject' | 'date_echeance'>;
export type PlantationSelectOption = Pick<Plantation, 'id' | 'address' | 'ville'>;
export type PlantationTableItem = Pick<Plantation, 'id' | 'address' | 'ville' | 'pays' | 'count_subject' | 'hauteur' | 'diametre' | 'date_echeance' | 'created_at' | 'updated_at'>;

export type PlantationResponse = z.infer<typeof PlantationResponseSchema>;
export type PlantationListResponse = z.infer<typeof PlantationListResponseSchema>;

export const validatePlantationCreate = (data: unknown) => {
  return PlantationCreateSchema.safeParse(data);
};

export const validatePlantationUpdate = (data: unknown) => {
  return PlantationUpdateSchema.omit({ id: true }).safeParse(data);
};

export const validatePlantationSelect = (data: unknown) => {
  return PlantationSelectSchema.safeParse(data);
};
